/** Adapts the browser Push API into the notification module's narrow interface. */
import type { PushBrowser, StoredPushSubscription } from '../pushNotifications.js';

const decodeApplicationServerKey = (value: string): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const decoded = atob(`${value}${padding}`.replaceAll('-', '+').replaceAll('_', '/'));
  const buffer = new ArrayBuffer(decoded.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
};

const storedSubscription = (subscription: PushSubscription): StoredPushSubscription => {
  const serialized = subscription.toJSON();
  const p256dh = serialized.keys?.['p256dh'];
  const auth = serialized.keys?.['auth'];
  if (typeof p256dh !== 'string' || typeof auth !== 'string') {
    throw new Error('The browser returned an incomplete Push subscription.');
  }

  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime,
    keys: { p256dh, auth },
  };
};

const isSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const createBrowserPush = (): PushBrowser => ({
  isSupported,
  permission: () => Notification.permission,
  requestPermission: () => Notification.requestPermission(),
  getSubscription: async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration === undefined) return null;
    const subscription = await registration.pushManager.getSubscription();
    return subscription === null ? null : storedSubscription(subscription);
  },
  subscribe: async (publicKey) => {
    const registration = await navigator.serviceWorker.ready;
    const current = await registration.pushManager.getSubscription();
    const subscription =
      current ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeApplicationServerKey(publicKey),
      }));
    return storedSubscription(subscription);
  },
  unsubscribe: async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration === undefined) return true;
    const subscription = await registration.pushManager.getSubscription();
    return subscription === null ? true : subscription.unsubscribe();
  },
});
